import { BookmarkSquareIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'pollworker/src/hooks';
import { loadElections, setElectionId } from 'pollworker/src/state/actions';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/src/components/ui/new';

export default function ElectionList() {
  const elections = useAppSelector((state) => state.election.elections);
  const loading = useAppSelector((state) => state.election.loading);
  const selectedElectionId = useAppSelector((state) => state.election.electionId);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(loadElections());
  }, [dispatch]);

  useEffect(() => {
    if (!elections?.length || selectedElectionId) return;

    const params = new URLSearchParams(window.location.search);
    const urlElectionId = params.get('electionId');
    const validUrlElection = urlElectionId && elections.some((election) => election.Id === urlElectionId);

    dispatch(setElectionId(validUrlElection ? urlElectionId : elections[0].Id));
  }, [dispatch, elections, selectedElectionId]);

  const handleElectionChange = useCallback(
    (newElectionId: string) => {
      dispatch(setElectionId(newElectionId));

      const url = new URL(window.location.href);
      url.searchParams.set('electionId', newElectionId);
      window.history.replaceState({}, '', url.toString());
    },
    [dispatch],
  );

  const selectedElection = useMemo(() => {
    if (!elections?.length) return;
    return elections.find((election) => election.Id === selectedElectionId) ?? elections[0];
  }, [elections, selectedElectionId]);

  return (
    <Select value={selectedElection?.Id ?? ''} onValueChange={handleElectionChange} disabled={!selectedElection}>
      <SelectTrigger size="sm" data-testid="elections-list" aria-label="select an election" className="truncate max-w-[8.5rem] font-semibold sm:max-w-52">
        <BookmarkSquareIcon className="hidden size-5 shrink-0 sm:block" aria-hidden="true" />
        <SelectValue className="truncate" placeholder={loading ? 'Loading…' : 'Select election'} />
      </SelectTrigger>
      <SelectContent align="end" className="max-w-[14em] sm:max-w-[20em]">
        {elections?.map((election) => (
          <SelectItem
            title={election.ElectionName}
            key={election.Id}
            value={election.Id}
            className="[&>span:last-child]:flex [&>span:last-child]:min-w-0 [&>span:last-child]:items-center truncate"
          >
            {election.ElectionName}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
